import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../firebase';
import { bankingSyncNowUrl } from '../../utils/apiBase';
import {
  BANK_PROVIDERS,
  BANK_PROVIDER_LABELS,
  KNOWN_BRANCHES,
  resolveBankingId,
  branchLabel,
  providerBankStateDocPath,
  providerBankMovementsCollectionPath
} from '../../utils/bankingSchema';
import { isMockBankingRecord, isMockBankingState } from '../../utils/bankingMockUtils';
import { BANKA_MASRAF_ETIKETI, isBankFeeMovement, summarizeBankFees } from '../../utils/bankMovementFees';
import { formatBankingDateTime, formatBankingDatePart, formatBankingTimePart, formatBankingValueDate, getIstanbulDayKey, toJsDate } from '../../utils/bankingDateTime';
import { extractGarantiEnrichmentFromRaw, stripBankTitle } from '../../utils/bankingMovementUi';
import { applyOwnAccountPartyLabels } from '../../utils/ownBankAccounts';
import { resolveCounterpartyTaxIdFromBankMovement } from '../../utils/bankMovementTaxId';
import { getMukellefDurumlari, MUKELLEF_DURUM, mukellefRozetStili, sorgulaMukellef } from '../../utils/mukellefDurumu';
import { subscribeIdentityPool } from '../../utils/identityClient';
import {
  buildMusteriTaxIndex,
  mapIdentityPoolToTaxRows,
  resolveDisplayTaxIdForMovement,
  upsertMusterilerFromBankMovements
} from '../../utils/musteriAutoFromBank';
import BankLogo from './BankLogo';

const BALANCE_CARD_BRANCH_ORDER = ['kavakli_sube', 'sefakoy_merkez', 'sefakoy_sube'];

const BALANCE_CARD_CONFIG = BALANCE_CARD_BRANCH_ORDER.flatMap((branchKey) => {
  const branch = KNOWN_BRANCHES.find((b) => b.key === branchKey);
  if (!branch) return [];
  return [
    { branchKey: branch.key, branchLabel: branch.label, provider: BANK_PROVIDERS.VAKIFBANK, accent: '#d97706' },
    { branchKey: branch.key, branchLabel: branch.label, provider: BANK_PROVIDERS.GARANTI, accent: '#15803d' }
  ];
});

const branchBalanceMetaKey = (branchKey, provider) => `${branchKey}:${provider}`;

const normalizeBankMovement = (m, provider, bankingId) => {
  const isGaranti = provider === BANK_PROVIDERS.GARANTI;
  const bank = isGaranti ? BANK_PROVIDERS.GARANTI : (m.bank || m.provider || provider);
  const bankLabel = isGaranti
    ? BANK_PROVIDER_LABELS[BANK_PROVIDERS.GARANTI]
    : (m.bankLabel || m.bankName || BANK_PROVIDER_LABELS[bank] || bank);
  return {
    ...m,
    bankingId: m.bankingId || bankingId,
    bank,
    bankLabel,
    bankLogoKey: m.bankLogoKey || bank,
    provider: isGaranti ? BANK_PROVIDERS.GARANTI : (m.provider || bank),
    bankName: isGaranti ? BANK_PROVIDER_LABELS[BANK_PROVIDERS.GARANTI] : (m.bankName || bankLabel)
  };
};

const resolveGarantiBalance = (state) => {
  if (isMockBankingState(state)) return 0;
  const raw = state?.currentBalance ?? state?.balance ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const matchesBankFilter = (row, filter) => {
  if (filter === 'all') return true;
  const bank = String(row?.bank || '').trim().toLowerCase();
  const prov = String(row?.provider || '').trim().toLowerCase();
  const target = String(filter || '').trim().toLowerCase();
  return bank === target || prov === target;
};

const isMockMovement = (row) => isMockBankingRecord(row);

const resolveVakifBalance = (state) => {
  if (isMockBankingState(state)) return 0;
  const n = Number(state?.currentBalance ?? state?.balance ?? 0);
  return Number.isFinite(n) ? n : 0;
};

// Açıklama metninden taraf/banka/IBAN bilgisini ayrıştırır (Firestore verisi değişmez; sadece görüntüde parse).
// Desteklenen kalıplar:
//  - "MEHMET DEMİRSOY TR42... hesabından TR72... hesabına ... virman"  (IBAN'lı)
//  - "MEHMET DEMİRSOY hesabından Kuveyt Türk Katılım Bankası S. M. Talha Gökdemir hesabına giden FAST ödemesi" (isimli)
const IBAN_REGEX = /TR\d{2}(?:\s?\d){22}/gi;
// Banka çekirdek adları; opsiyonel "Türkiye"/"T.C." önek ve "Bankası A.Ş." son ekleriyle sarmalanır.
const BANK_CORE = [
  'Kuveyt Türk',
  'Türkiye Finans',
  'Ziraat',
  'VakıfBank',
  'Vakıf(?:bank)?',
  'Garanti(?: BBVA)?',
  'Yapı(?: ve)? Kredi',
  'QNB(?: Finansbank)?',
  'Finansbank',
  'Denizbank',
  'Türk Ekonomi',
  'TEB',
  'Halkbank',
  'Halk',
  'Albaraka(?: Türk)?',
  'Şekerbank',
  'İş',
  'ING',
  'HSBC',
  'Fibabanka',
  'Odeabank',
  'Anadolubank',
  'Burgan',
  'Akbank',
  'Enpara(?:\\.com)?'
].join('|');
const BANK_NAME_REGEX = new RegExp(
  `(?:T\\.?C\\.? )?(?:Türkiye )?(?:${BANK_CORE})(?: Katılım)?(?: Bankası)?(?: A\\.?[ŞS]\\.?| Anonim Şirketi)?`,
  'i'
);

// UKBA'nın kendi açıklama ön ekleri / gürültü kalıpları (isim ayıklamadan önce temizlenir).
const stripNoisePhrases = (s) =>
  String(s || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/elden teslim (al[ıi]nan|edilen) k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/tcmb fast [üu]creti[\s\S]*?tutar[ıi](n[ıi]n)?/gi, ' ')
    .replace(/\bfast [öo]demesi\b/gi, ' ')
    .replace(/\b(virman|referans|nolu|no'lu|numaral[ıi]|sorgu|tarihli)\b/gi, ' ')
    .replace(/(^|\s)(bankas[ıi]|bank|kat[ıi]l[ıi]m)(?=\s|$)/gi, ' ')
    .replace(/\bT?\.?\s*A\.?\s*[ŞS]\.?(?=\s|$)/g, ' ')
    .replace(/\bAnonim [ŞS]irketi\b/gi, ' ')
    .replace(/\b(giden|gelen)\b/gi, ' ')
    .replace(/\b\d[\d./:-]{4,}\b/g, ' ');

const OUR_ACCOUNT_HOLDER = 'Mehmet Demirsoy';

const isIbanLike = (value) => /^TR\d{2}/i.test(String(value || '').replace(/\s/g, ''));

const looksLikePersonName = (value) => {
  const s = String(value || '').trim();
  if (!s || isIbanLike(s)) return false;
  if (/^\d+$/.test(s.replace(/\s/g, ''))) return false;
  const letters = s.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, '');
  return letters.length >= 4;
};

// DİKKAT: bu ekran parseMovementParties/resolveMovementDetail mantığının kendi
// kopyasını taşıyor. Banka ünvanı temizliği tek yerden gelsin diye ortak
// yardımcı (stripBankTitle) kullanılır; 19.10.2026'da ortak dosya düzeltilip
// bu kopya unutulduğu için ekranda "Bankası A.Ş. MEHMET DEMİRSOY" kalmıştı.
const sanitizePartyName = (value) => {
  const s = stripBankTitle(String(value || '').trim());
  return looksLikePersonName(s) ? s : '';
};

const parseMovementParties = (description) => {
  const text = String(description || '').replace(/\s+/g, ' ').trim();
  const empty = { senderName: '', receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
  if (!text) return empty;

  const cleanSpaces = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const firstIban = (s) => {
    const m = String(s || '').match(IBAN_REGEX);
    return m && m[0] ? cleanSpaces(m[0]) : '';
  };
  const stripIbans = (s) => String(s || '').replace(IBAN_REGEX, ' ');
  const nameClean = (raw) => {
    const cleaned = cleanSpaces(stripNoisePhrases(stripIbans(raw))).replace(/^[-–:,\s]+|[-–:,\s]+$/g, '').trim();
    const words = cleaned.split(' ').filter(Boolean);
    return words.length > 4 ? words.slice(-4).join(' ') : cleaned;
  };

  const fastMatch = text.match(/FAST\d+-([^-]+?)-/i);
  if (fastMatch) {
    const person = nameClean(fastMatch[1]);
    if (looksLikePersonName(person)) {
      return { senderName: person, receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
    }
  }

  const intHvlMatch = text.match(/INT-HVL-+\s*-?\s*([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü\s.]{2,})/i);
  if (intHvlMatch) {
    const person = nameClean(intHvlMatch[1]);
    if (looksLikePersonName(person)) {
      return { senderName: person, receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
    }
  }

  const fromMatch = text.match(/hesab[ıi]ndan/i);
  const toMatch = text.match(/hesab[ıi]na/i);

  let senderRaw = '';
  let receiverRaw = '';
  if (fromMatch) {
    senderRaw = text.slice(0, fromMatch.index);
    if (toMatch && toMatch.index > fromMatch.index) {
      receiverRaw = text.slice(fromMatch.index + fromMatch[0].length, toMatch.index);
    }
  } else {
    const rawMatches = text.match(IBAN_REGEX) || [];
    if (rawMatches[0]) {
      const i0 = text.indexOf(rawMatches[0]);
      senderRaw = text.slice(0, i0);
      if (rawMatches[1]) {
        const i1 = text.indexOf(rawMatches[1], i0 + rawMatches[0].length);
        receiverRaw = text.slice(i0 + rawMatches[0].length, i1);
      }
    }
  }

  const senderIban = firstIban(senderRaw);
  const receiverIban = firstIban(receiverRaw);
  const bankMatch = (receiverRaw && receiverRaw.match(BANK_NAME_REGEX)) || text.match(BANK_NAME_REGEX);
  const bankName = bankMatch ? cleanSpaces(bankMatch[0]) : '';

  const senderName = nameClean(String(senderRaw).replace(BANK_NAME_REGEX, ' '));
  const receiverName = nameClean(String(receiverRaw).replace(BANK_NAME_REGEX, ' '));

  return { senderName, receiverName, bankName, senderIban, receiverIban };
};

const namesEqual = (a, b) =>
  String(a || '').trim().toLocaleLowerCase('tr-TR') === String(b || '').trim().toLocaleLowerCase('tr-TR');

// rawData içinden olası anahtar adlarından ilk dolu değeri döndürür.
const pickRaw = (rawData, keys) => {
  if (!rawData || typeof rawData !== 'object') return '';
  for (const k of keys) {
    const v = rawData[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

// Normalize alanlar -> rawData enrichment -> açıklama parse sırasıyla modal alanlarını çözer.
const resolveMovementDetail = (movement) => {
  const raw = movement?.rawData || {};
  const parsed = parseMovementParties(movement?.description);
  const direction = movement?.direction;
  const enriched = extractGarantiEnrichmentFromRaw({
    ...raw,
    explanation: raw.explanation || movement?.description || '',
    customerName: raw.customerName || movement?.customerName
  });
  const parsedPerson =
    sanitizePartyName(parsed.senderName) || sanitizePartyName(parsed.receiverName) || '';

  let senderName =
    sanitizePartyName(movement?.senderName) ||
    sanitizePartyName(pickRaw(raw, ['gonderenAdi', 'gonderenAd', 'senderName', 'gonderen'])) ||
    '';
  let receiverName =
    sanitizePartyName(movement?.receiverName) ||
    sanitizePartyName(pickRaw(raw, ['aliciAdi', 'aliciAd', 'receiverName', 'alici', 'karsiUnvan', 'karsiAd'])) ||
    '';

  if (direction === 'out') {
    senderName = sanitizePartyName(enriched.ownName) || OUR_ACCOUNT_HOLDER;
    const receiverCandidate =
      receiverName ||
      sanitizePartyName(enriched.corrName) ||
      sanitizePartyName(parsed.receiverName) ||
      (sanitizePartyName(parsed.senderName) && !namesEqual(parsed.senderName, OUR_ACCOUNT_HOLDER)
        ? sanitizePartyName(parsed.senderName)
        : '') ||
      (parsedPerson && !namesEqual(parsedPerson, OUR_ACCOUNT_HOLDER) ? parsedPerson : '');
    receiverName = receiverCandidate;
  } else if (direction === 'in') {
    const senderCandidate =
      senderName ||
      sanitizePartyName(enriched.corrName) ||
      sanitizePartyName(parsed.senderName) ||
      (sanitizePartyName(parsed.receiverName) && !namesEqual(parsed.receiverName, OUR_ACCOUNT_HOLDER)
        ? sanitizePartyName(parsed.receiverName)
        : '') ||
      parsedPerson;
    senderName = senderCandidate;
    if (!receiverName) {
      receiverName = sanitizePartyName(enriched.ownName) || OUR_ACCOUNT_HOLDER;
    }
  } else {
    if (!senderName && parsedPerson) senderName = parsedPerson;
  }

  const ownIban = String(raw?.IBAN || raw?.iban || movement?.iban || '').trim();
  const corrIban = enriched.corrIban || '';

  let senderIban = movement?.senderIban || pickRaw(raw, ['gonderenIban', 'senderIban']) || parsed.senderIban || '';
  let receiverIban =
    movement?.receiverIban ||
    pickRaw(raw, ['aliciIban', 'receiverIban', 'karsiIban', 'karsiHesapIban']) ||
    parsed.receiverIban ||
    '';

  if (direction === 'in') {
    if (!senderIban) senderIban = corrIban;
    if (!receiverIban) receiverIban = ownIban;
  } else if (direction === 'out') {
    if (!senderIban) senderIban = ownIban;
    if (!receiverIban) receiverIban = corrIban;
  } else {
    if (!senderIban) senderIban = corrIban || pickRaw(raw, ['corrIBAN', 'corrIban']);
  }

  const senderAccount =
    movement?.senderAccount ||
    (direction === 'in' ? enriched.corrAccount : '') ||
    pickRaw(raw, ['gonderenHesap', 'senderAccount', 'corrAccountNum']) ||
    senderIban ||
    '';
  const receiverAccount =
    movement?.receiverAccount ||
    (direction === 'out' ? enriched.corrAccount : '') ||
    pickRaw(raw, ['aliciHesap', 'receiverAccount']) ||
    receiverIban ||
    pickRaw(raw, ['karsiHesap', 'karsiHesapNo']) ||
    '';

  const labeled = applyOwnAccountPartyLabels({
    senderName,
    receiverName,
    senderIban,
    receiverIban,
    ownIban,
    direction
  });

  /**
   * Banka masrafında karşı taraf diye "KESİNTİ VE EKLERİ BE" ya da
   * "HESABINDAN TAHSİLİ" görünüyordu; bunlar kişi değil, bankanın kestiği
   * ücretin açıklama kırıntısı. Ekranda anlaşılır bir ad yazılıyor —
   * hareketin kendisine dokunulmuyor, yalnız gösterim.
   */
  const masrafMi = isBankFeeMovement(movement);

  return {
    bankName: movement?.bankLabel || movement?.bankName || pickRaw(raw, ['bankaAdi', 'bankName']) || parsed.bankName || '',
    senderName: labeled.senderName,
    receiverName: masrafMi ? BANKA_MASRAF_ETIKETI : labeled.receiverName,
    senderIban,
    receiverIban,
    senderAccount,
    receiverAccount,
    transactionDate: formatBankingDatePart(movement),
    transactionTime: formatBankingTimePart(movement),
    valueDate: formatBankingValueDate(movement),
    directionLabel: movement?.direction === 'in' ? 'Gelen' : movement?.direction === 'out' ? 'Giden' : '-'
  };
};

// lastErrorCode / lastErrorMessage -> kısa, kullanıcı dostu mesaj.
const friendlyBankError = (code, message) => {
  const c = String(code || '').toLowerCase();
  const m = String(message || '').toLowerCase();
  if (c.includes('vbh0014') || m.includes('limit')) return 'Günlük sorgu limiti aşıldı';
  if (c.includes('permission')) return 'Bu şubenin banka verisine erişim yetkiniz yok';
  if (c.includes('timeout') || m.includes('timeout') || m.includes('ulaşıl') || m.includes('etimedout')) {
    return 'Banka servisine ulaşılamadı';
  }
  return 'Banka verisi alınamadı';
};

const resolveProviderSyncChip = (state) => {
  const s = String(state?.syncStatus || '').toLowerCase();
  if (!state) return { label: 'Bekleniyor', color: 'default', hasError: false };
  if (s.includes('error') || s.includes('hata')) return { label: 'Hata', color: 'error', hasError: true };
  if (s.includes('awaiting-live')) return { label: 'Bekleniyor', color: 'default', hasError: false };
  if (s.includes('mock')) return { label: 'Mock', color: 'warning', hasError: false };
  if (s.includes('live') || s.includes('ok') || s.includes('success')) return { label: 'Canlı', color: 'success', hasError: false };
  return { label: 'Bekleniyor', color: 'default', hasError: false };
};

const resolveTimestampMs = (value) => {
  if (!value) return 0;
  const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

function LiveBankModal({ open, onClose, sube }) {
  const [liveState, setLiveState] = useState(null);
  const [garantiLiveState, setGarantiLiveState] = useState(null);
  const [vakifMovements, setVakifMovements] = useState([]);
  const [garantiMovements, setGarantiMovements] = useState([]);
  const [allBranchStates, setAllBranchStates] = useState({});
  const [allGarantiBranchStates, setAllGarantiBranchStates] = useState({});
  const [branchBalanceMeta, setBranchBalanceMeta] = useState({});
  const [liveLoading, setLiveLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  /**
   * "Şimdi senkronize et" isteğinin hatası ayrı tutulur: bir kez başarısız olan istek
   * (ör. anlık ağ kopması) sonraki başarılı senkronda temizlenir. Eskiden loadError'a
   * yazılıyordu ve kullanıcı kapatana kadar veri güncel olsa bile uyarı ekranda kalıyordu.
   */
  const [syncError, setSyncError] = useState({ text: '', detail: '' });
  const syncInFlightRef = useRef(false);
  const [timeFilter, setTimeFilter] = useState('7d');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [bankFilter, setBankFilter] = useState('all');
  const [rangeStartDate, setRangeStartDate] = useState('');
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [showRawData, setShowRawData] = useState(false);
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [musteriTaxIndex, setMusteriTaxIndex] = useState(() => buildMusteriTaxIndex([]));
  const rowsPerPage = 8;
  const musteriAutoRunRef = useRef('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
    });
    return unsub;
  }, []);

  // On-demand banka senkronu: önce aktif şube (hızlı), diğer şubeler arka planda.
  const triggerSync = useCallback(async () => {
    if (!open || !sube) return;
    // Aynı anda ikinci istek atılmaz (açılış + Yenile çakışması)
    if (syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    setRefreshing(true);
    const requestOnce = async () => {
      const user =
        firebaseUser ||
        auth.currentUser ||
        (await new Promise((resolve) => {
          const unsub = onAuthStateChanged(auth, (nextUser) => {
            unsub();
            resolve(nextUser);
          });
        }));
      if (!user) return { ok: false, text: 'Oturum gerekli. Çıkış yapıp tekrar giriş yapın.', detail: 'no-user', retry: false };
      const token = await user.getIdToken();
      const res = await fetch(bankingSyncNowUrl(sube, { allBranches: true, force: true }), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok || data?.ok === false) {
        return {
          ok: false,
          text: friendlyBankError(data?.code, data?.message),
          detail: `HTTP ${res.status}${data?.code ? ` · ${data.code}` : ''}${data?.errorDetail || data?.message ? ` · ${data.errorDetail || data.message}` : ''}`,
          retry: res.status >= 500
        };
      }
      return { ok: true };
    };
    try {
      let result;
      try {
        result = await requestOnce();
      } catch (error) {
        result = { ok: false, text: friendlyBankError('network', error?.message), detail: `Ağ hatası · ${error?.message || error}`, retry: true };
      }
      // Anlık ağ / sunucu hatasında bir kez daha dene; ancak ikinci deneme de başarısızsa uyar
      if (!result.ok && result.retry) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          result = await requestOnce();
        } catch (error) {
          result = { ok: false, text: friendlyBankError('network', error?.message), detail: `Ağ hatası · ${error?.message || error}` };
        }
      }
      if (result.ok) {
        setSyncError({ text: '', detail: '' });
      } else {
        console.warn('[LiveBankModal] banka senkronu başarısız', result.detail);
        setSyncError({ text: result.text, detail: result.detail || '' });
      }
    } finally {
      syncInFlightRef.current = false;
      setRefreshing(false);
    }
  }, [open, sube, firebaseUser]);

  const formatMoney = (v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = toJsDate(value);
    if (!d) return '-';
    return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const resolveMovementDate = (row) => toJsDate(row?.bookingDate || row?.valueDate);

  const mergedMovements = useMemo(() => {
    const activeBankingId = resolveBankingId(sube);
    const tagged = [
      ...vakifMovements.map((m) => normalizeBankMovement(m, BANK_PROVIDERS.VAKIFBANK, activeBankingId)),
      ...garantiMovements.map((m) => normalizeBankMovement(m, BANK_PROVIDERS.GARANTI, activeBankingId))
    ].filter((m) => !isMockMovement(m));
    const seen = new Set();
    const deduped = tagged.filter((m) => {
      const key = `${m.provider || m.bank}|${m.bankingId}|${m.externalTxId || m.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return deduped.sort((a, b) => {
      const ta = resolveMovementDate(a)?.getTime() || 0;
      const tb = resolveMovementDate(b)?.getTime() || 0;
      if (tb !== ta) return tb - ta;
      return String(b.id || '').localeCompare(String(a.id || ''));
    });
  }, [vakifMovements, garantiMovements, sube]);

  // Bankadan gelen/giden karşı taraf → müşteri oluştur / TC doldur
  // Not: musteriTaxIndex bilerek dependency değil — yüklenince effect iptal + aynı imza
  // yüzünden TC doldurma hiç çalışmıyordu (Rabia/Cemal gibi isim-only kayıtlar boş kalıyordu).
  useEffect(() => {
    if (!open) {
      musteriAutoRunRef.current = '';
      return undefined;
    }
    if (!sube || !firebaseUser || liveLoading) return undefined;
    if (!mergedMovements.length) return undefined;

    const candidates = mergedMovements.filter((m) => {
      const tax = resolveCounterpartyTaxIdFromBankMovement(m);
      return Boolean(tax.tckn || tax.display || m.senderName || m.receiverName);
    });
    if (!candidates.length) return undefined;

    // v4: banka etiketleri (KESİNTİ / ALTIN ALIŞ) müşteri olmaz + junk cleanup
    const signature = `v4:${sube}:${candidates
      .slice(0, 50)
      .map((m) => `${m.id || m.externalTxId}:${resolveCounterpartyTaxIdFromBankMovement(m).tckn || m.receiverName || m.senderName || ''}`)
      .join('|')}`;
    if (musteriAutoRunRef.current === signature) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const stats = await upsertMusterilerFromBankMovements({
          sube,
          movements: candidates
        });
        if (cancelled) return;
        musteriAutoRunRef.current = signature;
        const parts = [];
        if (stats.created) parts.push(`${stats.created} yeni müşteri`);
        if (stats.tc_updated) parts.push(`${stats.tc_updated} TC güncellendi`);
        if (stats.deleted) parts.push(`${stats.deleted} hatalı kayıt silindi`);
        if (stats.errors) parts.push(`${stats.errors} hata`);
        if (parts.length) {
          toast.success(`Müşteriler: ${parts.join(', ')}`);
        }
      } catch (err) {
        console.warn('[LiveBankModal] musteri auto', err);
      }
    }, 800);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, sube, firebaseUser, liveLoading, mergedMovements]);

  /**
   * TC indeksi — bankada TC yoksa isimle tamamlamak için.
   *
   * İki kaynak birleşiyor:
   *  1) müşteri kartları (elle girilen, asıl kaynak)
   *  2) kimlik havuzu (gelen havalelerden öğrenilen TC'ler)
   *
   * İkincisi özellikle GİDEN ödemelerde işe yarıyor: banka alıcının TC'sini
   * göndermiyor, ama aynı kişi daha önce bize para gönderdiyse TC'si havuzda var.
   */
  useEffect(() => {
    if (!open || !sube) {
      setMusteriTaxIndex(buildMusteriTaxIndex([]));
      return undefined;
    }

    let musteriRows = [];
    let poolRows = [];
    const rebuild = () => setMusteriTaxIndex(buildMusteriTaxIndex([...musteriRows, ...poolRows]));

    const unsubMusteri = onSnapshot(
      query(collection(db, 'subeler', sube, 'musteriler')),
      (snap) => {
        musteriRows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rebuild();
      },
      (err) => console.warn('[LiveBankModal] musteri index', err?.message || err)
    );

    const unsubPool = subscribeIdentityPool(
      sube,
      (rows) => {
        poolRows = mapIdentityPoolToTaxRows(rows);
        rebuild();
      },
      (err) => console.warn('[LiveBankModal] kimlik havuzu index', err?.message || err)
    );

    return () => {
      unsubMusteri();
      unsubPool?.();
    };
  }, [open, sube]);

  useEffect(() => {
    if (!open || !sube) return undefined;
    setLiveLoading(true);
    setLoadError('');
    setSyncError({ text: '', detail: '' });
    const bankingId = resolveBankingId(sube);
    const statePath = providerBankStateDocPath(bankingId, 'garanti');
    const movementPath = providerBankMovementsCollectionPath(bankingId, 'garanti');

    const vakifStateRef = doc(db, 'subeler', bankingId, 'bankState', 'current');
    const garantiStateRef = doc(db, 'subeler', bankingId, 'bankProviders', 'garanti', 'bankState', 'current');
    const vakifMovementsQ = query(
      collection(db, 'subeler', bankingId, 'bankMovements'),
      orderBy('bookingDate', 'desc'),
      limit(500)
    );
    const garantiMovementsQ = query(
      collection(db, 'subeler', bankingId, 'bankProviders', 'garanti', 'bankMovements'),
      orderBy('bookingDate', 'desc'),
      limit(500)
    );

    const buildError = (err, fallback) =>
      err?.code === 'permission-denied'
        ? 'Bu şubenin banka verisine erişim yetkiniz yok.'
        : fallback;

    let pending = 4;
    const markLoaded = () => {
      pending -= 1;
      if (pending <= 0) setLiveLoading(false);
    };

    const u1 = onSnapshot(vakifStateRef, (snap) => {
      setLiveState(snap.exists() ? snap.data() : null);
      markLoaded();
    }, (err) => {
      setLiveState(null);
      setLoadError(buildError(err, 'VakıfBank durum verisi okunamadı.'));
      markLoaded();
    });

    const u2 = onSnapshot(vakifMovementsQ, (snap) => {
      setVakifMovements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      markLoaded();
    }, (err) => {
      setVakifMovements([]);
      setLoadError(buildError(err, 'VakıfBank hareketleri okunamadı.'));
      markLoaded();
    });

    const u3 = onSnapshot(garantiStateRef, (snap) => {
      setGarantiLiveState(snap.exists() ? snap.data() : null);
      markLoaded();
    }, (err) => {
      console.error('[LiveBankModal] garanti state error', {
        path: statePath,
        code: err?.code,
        message: err?.message
      });
      setLoadError(buildError(err, 'Garanti durum verisi okunamadı.'));
      markLoaded();
    });

    const u4 = onSnapshot(garantiMovementsQ, (snap) => {
      setGarantiMovements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      markLoaded();
    }, (err) => {
      console.error('[LiveBankModal] garanti movements error', {
        path: movementPath,
        code: err?.code,
        message: err?.message
      });
      setLoadError(buildError(err, 'Garanti hareketleri okunamadı.'));
      markLoaded();
    });

    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }, [open, sube]);

  useEffect(() => {
    if (!open) return undefined;
    const setMeta = (branchKey, provider, status) => {
      setBranchBalanceMeta((prev) => ({
        ...prev,
        [branchBalanceMetaKey(branchKey, provider)]: status
      }));
    };
    const unsubs = KNOWN_BRANCHES.flatMap((b) => {
      const bankingId = resolveBankingId(b.key);
      const uVakif = onSnapshot(
        doc(db, 'subeler', bankingId, 'bankState', 'current'),
        (snap) => {
          setAllBranchStates((prev) => ({ ...prev, [b.key]: snap.exists() ? snap.data() : null }));
          setMeta(b.key, BANK_PROVIDERS.VAKIFBANK, snap.exists() ? 'loaded' : 'missing');
        },
        (err) => {
          setAllBranchStates((prev) => ({ ...prev, [b.key]: null }));
          setMeta(b.key, BANK_PROVIDERS.VAKIFBANK, err?.code === 'permission-denied' ? 'denied' : 'error');
          if (err?.code !== 'permission-denied') {
            console.error('[LiveBankModal] vakif branch state error', {
              branchKey: b.key,
              bankingId,
              code: err?.code,
              message: err?.message
            });
          }
        }
      );
      const uGaranti = onSnapshot(
        doc(db, 'subeler', bankingId, 'bankProviders', 'garanti', 'bankState', 'current'),
        (snap) => {
          setAllGarantiBranchStates((prev) => ({ ...prev, [b.key]: snap.exists() ? snap.data() : null }));
          setMeta(b.key, BANK_PROVIDERS.GARANTI, snap.exists() ? 'loaded' : 'missing');
        },
        (err) => {
          setAllGarantiBranchStates((prev) => ({ ...prev, [b.key]: null }));
          setMeta(b.key, BANK_PROVIDERS.GARANTI, err?.code === 'permission-denied' ? 'denied' : 'error');
          if (err?.code !== 'permission-denied') {
            console.error('[LiveBankModal] garanti branch state error', {
              branchKey: b.key,
              bankingId,
              code: err?.code,
              message: err?.message
            });
          }
        }
      );
      return [uVakif, uGaranti];
    });
    return () => unsubs.forEach((u) => u && u());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTimeFilter('7d');
    setDirectionFilter('all');
    setBankFilter('all');
    setRangeStartDate('');
    setRangeEndDate('');
    setQueryText('');
    setAmountMin('');
    setAmountMax('');
    setSelectedMovement(null);
    setShowRawData(false);
    setPage(0);
  }, [open, sube]);

  // Sayfa açılışında tek sefer banka sorgusu; periyodik otomatik sorgu yok (kota koruması).
  // Yenile butonu triggerSync çağırır.
  useEffect(() => {
    if (!open || !sube) return undefined;
    triggerSync();
    return undefined;
  }, [open, sube, triggerSync]);

  const resolveBalanceCardDisplay = (branchKey, provider) => {
    const meta = branchBalanceMeta[branchBalanceMetaKey(branchKey, provider)];
    const state =
      provider === BANK_PROVIDERS.VAKIFBANK
        ? allBranchStates?.[branchKey]
        : allGarantiBranchStates?.[branchKey];

    if (meta === 'denied') return { kind: 'denied', text: 'Yetki yok', value: null };
    if (meta === 'error') return { kind: 'error', text: 'Veri okunamadı', value: null };
    if (meta === 'missing' || !state) {
      return { kind: 'missing', text: 'Henüz senkronize edilmedi', value: null };
    }
    const chip = resolveProviderSyncChip(state);
    if (chip.hasError) {
      return {
        kind: 'error',
        text: friendlyBankError(state?.lastErrorCode, state?.lastErrorMessage),
        value: null
      };
    }
    const value =
      provider === BANK_PROVIDERS.VAKIFBANK ? resolveVakifBalance(state) : resolveGarantiBalance(state);
    return { kind: 'balance', text: `${formatMoney(value)} TL`, value };
  };

  /**
   * Genel toplam + kaç hesabın toplama girdiği.
   *
   * Okunamayan hesap (yetki yok / hata / henüz senkron edilmemiş) toplamdan
   * çıkarılıyor. Rakam aynı hesaplanır ama artık eksik olduğunda bunu söyler —
   * eksik bir para tutarının doğruymuş gibi durması en tehlikelisiydi.
   */
  const generalTotal = useMemo(() => {
    let sum = 0;
    let included = 0;
    const missing = [];

    BALANCE_CARD_CONFIG.forEach((card) => {
      const meta = branchBalanceMeta[branchBalanceMetaKey(card.branchKey, card.provider)];
      const state =
        card.provider === BANK_PROVIDERS.VAKIFBANK
          ? allBranchStates?.[card.branchKey]
          : allGarantiBranchStates?.[card.branchKey];

      if (meta !== 'loaded' || !state) {
        missing.push(card);
        return;
      }
      const chip = resolveProviderSyncChip(state);
      if (chip.hasError) {
        missing.push(card);
        return;
      }
      const value =
        card.provider === BANK_PROVIDERS.VAKIFBANK
          ? resolveVakifBalance(state)
          : resolveGarantiBalance(state);
      sum += value;
      included += 1;
    });

    return {
      value: sum,
      included,
      total: BALANCE_CARD_CONFIG.length,
      missingCount: missing.length,
      complete: missing.length === 0
    };
  }, [branchBalanceMeta, allBranchStates, allGarantiBranchStates]);

  const generalTotalBalance = generalTotal.value;

  /**
   * Bugünün banka masrafı / POS komisyonu.
   * Filtrelerden BAĞIMSIZ: her zaman bugünün rakamını gösterir, gece yarısı
   * sıfırlanır, ertesi güne taşınmaz. Kullanıcı 7 Gün filtresindeyken bile
   * şerit "Bugün" der, karışmasın.
   */
  const todayFees = useMemo(() => {
    const todayKey = getIstanbulDayKey(new Date());
    const todays = (mergedMovements || []).filter((m) => {
      const d = resolveMovementDate(m);
      return d && getIstanbulDayKey(d) === todayKey;
    });
    return summarizeBankFees(todays);
  }, [mergedMovements]);

  const filteredMovements = useMemo(() => {
    const now = new Date();
    const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d1 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const d7 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    const d15 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14);
    const d30 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
    const rStart = rangeStartDate ? new Date(`${rangeStartDate}T00:00:00`) : null;
    const rEnd = rangeEndDate ? new Date(`${rangeEndDate}T23:59:59`) : null;
    const q = String(queryText || '').trim().toLowerCase();
    const min = amountMin === '' ? null : Number(amountMin);
    const max = amountMax === '' ? null : Number(amountMax);
    return mergedMovements.filter((row) => {
      const date = resolveMovementDate(row);
      if (!date) return false;
      if (!matchesBankFilter(row, bankFilter)) return false;
      if (timeFilter === 'today' && date < d0) return false;
      if (timeFilter === 'yesterday' && (date < d1 || date >= d0)) return false;
      if (timeFilter === '7d' && date < d7) return false;
      if (timeFilter === '15d' && date < d15) return false;
      if (timeFilter === '30d' && date < d30) return false;
      if (timeFilter === 'range' && rStart && rEnd && (date < rStart || date > rEnd)) return false;
      if (directionFilter !== 'all' && row.direction !== directionFilter) return false;
      const amount = Number(row.amount || 0);
      if (min != null && !Number.isNaN(min) && amount < min) return false;
      if (max != null && !Number.isNaN(max) && amount > max) return false;
      if (!q) return true;
      const tax = resolveDisplayTaxIdForMovement(row, musteriTaxIndex);
      const text = `${row.description || ''} ${row.reference || ''} ${row.externalTxId || ''} ${row.senderName || ''} ${row.receiverName || ''} ${tax.display || ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [mergedMovements, queryText, directionFilter, bankFilter, timeFilter, rangeStartDate, rangeEndDate, amountMin, amountMax, musteriTaxIndex]);

  const pagedMovements = useMemo(() => filteredMovements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredMovements, page]);

  // Gorunen satirlardaki musterilerin belge tipi (e-Fatura / e-Arsiv).
  // Sadece ekrandaki satirlar sorgulanir, sonuc bellekte tutulur.
  const [mukellefMap, setMukellefMap] = useState(() => new Map());
  const [mukellefSorgulanan, setMukellefSorgulanan] = useState(null);
  useEffect(() => {
    const ids = pagedMovements
      .map((row) => resolveDisplayTaxIdForMovement(row, musteriTaxIndex)?.display)
      .filter(Boolean);
    if (!ids.length) return;
    let iptal = false;
    getMukellefDurumlari(ids, sube)
      .then((map) => {
        if (iptal || !map.size) return;
        setMukellefMap((prev) => {
          const next = new Map(prev);
          map.forEach((v, k) => next.set(k, v));
          return next;
        });
      })
      .catch(() => {});
    return () => {
      iptal = true;
    };
  }, [pagedMovements, musteriTaxIndex, sube]);
  const parties = useMemo(() => resolveMovementDetail(selectedMovement), [selectedMovement]);
  const selectedTaxId = useMemo(
    () => resolveDisplayTaxIdForMovement(selectedMovement, musteriTaxIndex),
    [selectedMovement, musteriTaxIndex]
  );
  const statusChip = useMemo(() => {
    const vakif = resolveProviderSyncChip(liveState);
    const garanti = resolveProviderSyncChip(garantiLiveState);
    if (garanti.hasError && vakif.hasError) return { label: 'Hata', color: 'error' };
    if (garanti.hasError || vakif.hasError) return { label: 'Kısmi Hata', color: 'error' };
    if (garanti.label === 'Canlı') return { label: 'Canlı', color: 'success' };
    if (vakif.label === 'Canlı' && garanti.label !== 'Mock') return { label: 'Canlı', color: 'success' };
    if (garanti.label === 'Mock' && vakif.label === 'Mock') return { label: 'Mock', color: 'warning' };
    return { label: 'Bekleniyor', color: 'default' };
  }, [liveState, garantiLiveState]);

  const vakifIsMock = useMemo(() => isMockBankingState(liveState), [liveState]);

  const lastUpdatedAt = useMemo(() => {
    const ms = Math.max(
      resolveTimestampMs(liveState?.updatedAt),
      resolveTimestampMs(liveState?.lastSyncAt),
      resolveTimestampMs(garantiLiveState?.updatedAt),
      resolveTimestampMs(garantiLiveState?.lastSyncAt)
    );
    return ms ? new Date(ms) : null;
  }, [liveState, garantiLiveState]);

  // Firestore'da kayıtlı sync hatası varsa kullanıcı dostu metne çevir.
  const syncErrorText = useMemo(() => {
    const parts = [];
    const vakifStatus = String(liveState?.syncStatus || '').toLowerCase();
    if (vakifStatus.includes('error') || vakifStatus.includes('hata')) {
      parts.push(`VakıfBank: ${friendlyBankError(liveState?.lastErrorCode, liveState?.lastErrorMessage)}`);
    }
    const garantiStatus = String(garantiLiveState?.syncStatus || '').toLowerCase();
    if (garantiStatus.includes('error') || garantiStatus.includes('hata')) {
      parts.push(`Garanti: ${friendlyBankError(garantiLiveState?.lastErrorCode, garantiLiveState?.lastErrorMessage)}`);
    }
    return parts.join(' · ');
  }, [
    liveState?.syncStatus,
    liveState?.lastErrorCode,
    liveState?.lastErrorMessage,
    garantiLiveState?.syncStatus,
    garantiLiveState?.lastErrorCode,
    garantiLiveState?.lastErrorMessage
  ]);

  const clearFilters = () => {
    setQueryText('');
    setDirectionFilter('all');
    setBankFilter('all');
    setTimeFilter('7d');
    setRangeStartDate('');
    setRangeEndDate('');
    setDateRangeDialogOpen(false);
    setAmountMin('');
    setAmountMax('');
    setPage(0);
  };

  const summaryCardSx = {
    height: '100%',
    minHeight: 22,
    borderRadius: 2.5,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 14px rgba(15,23,42,0.05)',
    background: '#fff'
  };
  const modernMetricCardSx = (accent) => ({
    ...summaryCardSx,
    border: `1px solid ${accent}28`,
    background: '#fff',
    boxShadow: '0 4px 14px rgba(15,23,42,0.05)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'box-shadow 160ms ease, border-color 160ms ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 3,
      background: accent
    }
  });

  const filterToggleSx = {
    bgcolor: '#fff',
    borderRadius: 1.5,
    '& .MuiToggleButton-root': {
      minWidth: 78,
      px: 1.5,
      py: 0.55,
      fontWeight: 800,
      fontSize: 12.5,
      textTransform: 'none',
      color: '#475569',
      borderColor: '#dbe3ef',
      '&.Mui-selected': {
        color: '#fff',
        backgroundColor: '#0f766e',
        borderColor: '#0f766e',
        '&:hover': { backgroundColor: '#0d665f' }
      }
    }
  };

  const detailRow = (label, value, mono = false) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, px: 2.2, py: 1.25 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.04em', textTransform: 'uppercase', minWidth: 118, flexShrink: 0, pt: 0.2 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 700,
          color: '#0f172a',
          textAlign: 'right',
          wordBreak: 'break-word',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit'
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog className="lb-modal" fullScreen open={open} onClose={onClose}>
      <AppBar
        className="lb-appbar"
        position="sticky"
        sx={{
          bgcolor: '#1f2937',
          background: 'linear-gradient(90deg, #1f2937 0%, #111827 100%)',
          borderBottom: '1px solid rgba(148,163,184,0.22)'
        }}
      >
        <Toolbar className="lb-toolbar" sx={{ minHeight: 64, px: 1.8, gap: 1.2 }}>
          <AccountBalanceIcon className="lb-toolbar-icon" sx={{ color: '#93c5fd', fontSize: 23 }} />
          <Typography className="lb-toolbar-title" sx={{ fontWeight: 900, fontSize: 19, color: '#f8fafc' }}>Canlı Banka Akışı</Typography>
          <Chip className="lb-toolbar-branch" size="small" label={branchLabel(sube)} sx={{ bgcolor: 'rgba(59,130,246,0.16)', color: '#dbeafe', fontWeight: 800 }} />
          <Box
            className="lb-toolbar-warning"
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              px: 1,
              minWidth: 0
            }}
          >
            {(loadError || syncError.text || syncErrorText) ? (
              <Alert
                severity="warning"
                title={!loadError && syncError.text ? syncError.detail : undefined}
                onClose={
                  loadError
                    ? () => setLoadError('')
                    : syncError.text
                      ? () => setSyncError({ text: '', detail: '' })
                      : undefined
                }
                sx={{
                  py: 0,
                  px: 1.2,
                  maxWidth: 420,
                  width: 'auto',
                  bgcolor: 'rgba(251, 191, 36, 0.18)',
                  color: '#fef3c7',
                  border: '1px solid rgba(251, 191, 36, 0.45)',
                  '& .MuiAlert-icon': { color: '#fcd34d', py: 0.4 },
                  '& .MuiAlert-message': { fontSize: 12.5, fontWeight: 800, py: 0.35 },
                  '& .MuiAlert-action': { pt: 0.2, alignItems: 'center' }
                }}
              >
                {loadError || syncError.text || syncErrorText}
              </Alert>
            ) : null}
          </Box>
          <Typography className="lb-toolbar-sync" sx={{ fontSize: 12, color: '#cbd5e1', mr: 0.4, fontWeight: 700, whiteSpace: 'nowrap' }}>Son güncelleme: {formatDateTime(lastUpdatedAt)}</Typography>
          <Tooltip title="Yenile">
            <span>
              <Button
                className="lb-toolbar-refresh"
                onClick={triggerSync}
                disabled={refreshing}
                size="small"
                startIcon={refreshing ? <CircularProgress size={14} sx={{ color: '#93c5fd' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                sx={{
                  mr: 1,
                  color: '#dbeafe',
                  textTransform: 'none',
                  fontWeight: 800,
                  minWidth: 0,
                  px: 1.1,
                  border: '1px solid rgba(148,163,184,0.35)',
                  '&:hover': { bgcolor: 'rgba(59,130,246,0.16)' }
                }}
              >
                Yenile
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={syncErrorText || statusChip.label}>
            <Chip
              className="lb-toolbar-status"
              size="small"
              label={statusChip.label}
              color={statusChip.color}
              sx={{
                fontWeight: 800,
                '& .MuiChip-label': { color: '#f8fafc' }
              }}
            />
          </Tooltip>
          {vakifIsMock ? (
            <Typography sx={{ fontSize: 11, color: '#fcd34d', fontWeight: 700, whiteSpace: 'nowrap' }}>
              VakıfBank test verisi
            </Typography>
          ) : null}
          <IconButton className="lb-toolbar-close" edge="end" color="inherit" onClick={onClose}><CloseIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box className="lb-body" sx={{ position: 'relative', height: 'calc(100vh - 64px)', p: 1.2, overflow: 'auto', bgcolor: '#f1f5f9' }}>
        {liveLoading ? (
          <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className="lb-layout" sx={{ height: '100%', minHeight: 0, display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', gap: 1.1, minWidth: 0, width: '100%' }}>
            <Paper className="lb-balance-cards-wrap" variant="outlined" sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', p: 0.85, minWidth: 0, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.92)' }}>
              <Box
                className="lb-balance-cards-grid"
                sx={{
                  display: 'grid',
                  // Karta en az 250px: olculdu, sube adi (105px) + bakiye (110px)
                  // ancak bu genislikte kirpilmadan siginiyor (215px'de yazi
                  // sutununa 90px kaliyordu). Yer kalmayinca kartlar kendiliginden
                  // alt satira iner: 1200px'de 4 sutun, genis ekranda 7, telefonda 1.
                  // Eskiden MUI kirilma noktalariyla 1200px'de 7 sutun zorlaniyor
                  // ve hepsi kirpiliyordu: "K...", "S...", "3...".
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: 0.55,
                  minWidth: 0
                }}
              >
                {BALANCE_CARD_CONFIG.map((card) => {
                  const display = resolveBalanceCardDisplay(card.branchKey, card.provider);
                  const isActiveBranch = card.branchKey === sube;
                  const amountColor =
                    display.kind === 'balance'
                      ? '#111827'
                      : display.kind === 'denied'
                        ? '#9a3412'
                        : '#64748b';
                  return (
                    <Card
                      key={`${card.branchKey}-${card.provider}`}
                      sx={{
                        ...modernMetricCardSx(card.accent),
                        minHeight: 80,
                        minWidth: 0,
                        ...(isActiveBranch
                          ? {
                              border: `1.5px solid ${card.accent}`,
                              boxShadow: `0 8px 20px ${card.accent}28`
                            }
                          : {})
                      }}
                    >
                      <CardContent
                        sx={{
                          p: '5px 10px !important',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 1.5,
                          minWidth: 0,
                          '&:last-child': { pb: '5px !important' }
                        }}
                      >
                        <BankLogo bank={card.provider} size="card" />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 0.4,
                            alignItems: 'flex-start'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: '#334155',
                              lineHeight: 1.1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%'
                            }}
                          >
                            {card.branchLabel}
                          </Typography>
                          {isActiveBranch ? (
                            <Chip
                              size="small"
                              label="Aktif Şube"
                              sx={{
                                height: 21,
                                fontSize: 11,
                                fontWeight: 900,
                                bgcolor: `${card.accent}22`,
                                color: card.accent
                              }}
                            />
                          ) : null}
                          <Typography
                            sx={{
                              fontSize: 18,
                              fontWeight: 900,
                              color: amountColor,
                              lineHeight: 1.05,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%'
                            }}
                          >
                            {display.text}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
                {/* Toplam eksik hesapla hesaplandıysa kart bunu söyler; sessiz
                    bir eksik para rakamı doğruymuş gibi durmamalı. */}
                <Card
                  sx={{
                    ...summaryCardSx,
                    minHeight: 80,
                    minWidth: 0,
                    border: generalTotal.complete ? '1px solid #cbd5e1' : '1px solid #f59e0b',
                    background: generalTotal.complete
                      ? 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)'
                      : 'linear-gradient(145deg, #422006 0%, #78350f 100%)',
                    boxShadow: '0 8px 22px rgba(15,23,42,0.22)'
                  }}
                >
                  {/* Üst/alt boşluk görsel olarak eşitlensin: büyük harfli başlığın satır
                      kutusunda harflerin üstünde ~3px ölü alan var, bu yüzden üst dolgu kısık. */}
                  <CardContent sx={{ p: '3px 12px 7px !important', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 0.2, '&:last-child': { pb: '7px !important' } }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: generalTotal.complete ? '#cbd5e1' : '#fcd34d', letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.1 }}>
                      {generalTotal.complete ? 'Genel Toplam' : 'Genel Toplam · Eksik'}
                    </Typography>
                    <Typography sx={{ fontSize: 23, fontWeight: 900, color: '#f8fafc', lineHeight: 1.08 }}>
                      {formatMoney(generalTotalBalance)} TL
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: generalTotal.complete ? '#94a3b8' : '#fcd34d',
                        lineHeight: 1.2
                      }}
                    >
                      {generalTotal.complete
                        ? `${generalTotal.included}/${generalTotal.total} hesap`
                        : `${generalTotal.included}/${generalTotal.total} hesap · ${generalTotal.missingCount} hesap okunamadı`}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Paper>

            <Box className="lb-main-row" sx={{ minHeight: 0, minWidth: 0, width: '100%', display: 'grid', gridTemplateColumns: '1fr' }}>
              <Paper className="lb-transactions-panel" sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 18px rgba(15,23,42,0.04)', display: 'grid', gridTemplateRows: 'auto auto 1fr 52px', overflow: 'hidden', minHeight: 0, minWidth: 0, width: '100%' }}>
                <Box className="lb-transactions-header" sx={{ px: 1.5, py: 1, borderBottom: '1px solid #eef2f7', bgcolor: '#fff', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.01em' }}>
                    {branchLabel(sube)} Banka Hareketleri
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8' }}>
                    {filteredMovements.length} işlem
                  </Typography>
                </Box>
                <Box className="lb-filters" sx={{ p: 1.1, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 1, borderBottom: '1px solid #eef2f7', bgcolor: '#fafbfc', overflow: 'hidden' }}>
                <TextField
                  className="lb-search-field"
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  size="small"
                  placeholder="Arama: açıklama / referans / kişi / TC"
                  sx={{
                    // Satır sarmasın diye tek esneyen öğe arama kutusu; alt sınırı korunur.
                    flex: '1 1 220px',
                    minWidth: 130,
                    maxWidth: 300,
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                    '& .MuiInputBase-input': { fontSize: 13.5 },
                    '& .MuiInputBase-input::placeholder': { fontSize: 12.5, opacity: 0.9 }
                  }}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.8, color: '#94a3b8', fontSize: 18 }} /> }}
                />
                <Box className="lb-bank-filters" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <ToggleButtonGroup
                    className="lb-bank-toggle"
                    value={bankFilter}
                    exclusive
                    onChange={(_, value) => {
                      if (!value) return;
                      setBankFilter(value);
                      setPage(0);
                    }}
                    size="small"
                    sx={filterToggleSx}
                  >
                    <ToggleButton value="all">Tümü</ToggleButton>
                    <ToggleButton value={BANK_PROVIDERS.VAKIFBANK} aria-label="VakıfBank" title="VakıfBank">
                      <BankLogo bank={BANK_PROVIDERS.VAKIFBANK} size="xs" />
                    </ToggleButton>
                    <ToggleButton value={BANK_PROVIDERS.GARANTI} aria-label="Garanti BBVA" title="Garanti BBVA">
                      <BankLogo bank={BANK_PROVIDERS.GARANTI} size="xs" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box className="lb-direction-filters" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <ToggleButtonGroup
                    className="lb-direction-toggle"
                    value={directionFilter}
                    exclusive
                    onChange={(_, value) => value && (setDirectionFilter(value), setPage(0))}
                    size="small"
                    sx={filterToggleSx}
                  >
                    <ToggleButton value="all">Tümü</ToggleButton>
                    <ToggleButton value="in">Gelen</ToggleButton>
                    <ToggleButton value="out">Giden</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box className="lb-time-filters" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <ToggleButtonGroup
                    className="lb-time-toggle"
                    value={timeFilter}
                    exclusive
                    onChange={(_, value) => value && (setTimeFilter(value), setPage(0))}
                    size="small"
                    sx={filterToggleSx}
                  >
                    <ToggleButton value="today">Bugün</ToggleButton>
                    <ToggleButton value="yesterday">Dün</ToggleButton>
                    <ToggleButton value="7d">7 Gün</ToggleButton>
                    <ToggleButton value="15d">15 Gün</ToggleButton>
                    <ToggleButton value="30d">30 Gün</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    className="lb-range-btn"
                    size="small"
                    variant={timeFilter === 'range' ? 'contained' : 'outlined'}
                    onClick={() => setDateRangeDialogOpen(true)}
                    sx={{
                      minWidth: 100,
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 1.5,
                      ...(timeFilter === 'range'
                        ? { bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d665f' } }
                        : { borderColor: '#dbe3ef', color: '#475569' })
                    }}
                  >
                    Tarih Seç
                  </Button>
                  <Button
                    className="lb-clear-btn"
                    onClick={clearFilters}
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 800, color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.06)' } }}
                  >
                    Temizle
                  </Button>

                  {/* Bugünün banka masrafı / POS komisyonu — sağ uçta, TUTAR sütununun
                      üstünde. Filtrelerden bağımsız; gece yarısı sıfırlanır. */}
                  <Tooltip
                    placement="bottom-end"
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.7 }}>
                        <div>Bugün kesilen banka masrafları</div>
                        <div>· Havale/EFT, BSMV, damga: {todayFees.transferCount} hareket</div>
                        <div>· POS komisyonu: {todayFees.posCount} hareket</div>
                        <div style={{ opacity: 0.75, marginTop: 4 }}>
                          Garanti komisyonu açıklamadaki “K:” değerinden, VakıfBank ayrı komisyon
                          hareketinden alınır.
                        </div>
                      </Box>
                    }
                  >
                    <Box
                      className="lb-fee-strip"
                      sx={{
                        ml: 'auto',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.9,
                        px: 1.1,
                        py: 0.45,
                        borderRadius: 1.5,
                        // Masraf bir gider kalemi — filtre çubuğundan ayrışsın diye sıcak ton
                        border: '1px solid #fcd9a4',
                        bgcolor: '#fff7ed',
                        cursor: 'default',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#a16207', letterSpacing: '0.05em' }}>
                        BUGÜN
                      </Typography>
                      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: '#fcd9a4' }} />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#a16207' }}>
                        Havale/EFT masrafı{' '}
                        <Box component="span" sx={{ fontWeight: 800, color: '#78350f' }}>
                          {formatMoney(todayFees.transfer)}
                        </Box>
                      </Typography>
                      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: '#fcd9a4' }} />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#a16207' }}>
                        POS komisyonu{' '}
                        <Box component="span" sx={{ fontWeight: 800, color: '#78350f' }}>
                          {formatMoney(todayFees.pos)}
                        </Box>
                      </Typography>
                      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: '#fcd9a4' }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: '#b45309' }}>
                        Toplam {formatMoney(todayFees.total)} TL
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
                </Box>

                <TableContainer className="lb-table-wrap" sx={{ minHeight: 0 }}>
                  <Table className="lb-table" stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '12%', borderBottom: '1px solid #e2e8f0' }}>Tarih / Saat</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '9%', borderBottom: '1px solid #e2e8f0' }}>Banka</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '7%', borderBottom: '1px solid #e2e8f0' }}>Yön</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '12%', borderBottom: '1px solid #e2e8f0' }}>Gönderen</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '12%', borderBottom: '1px solid #e2e8f0' }}>Alıcı</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '12%', borderBottom: '1px solid #e2e8f0' }}>TC / VKN</TableCell>
                        <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '24%', borderBottom: '1px solid #e2e8f0' }}>Açıklama</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b', fontSize: 11.5, letterSpacing: '0.03em', textTransform: 'uppercase', width: '9%', pr: 2.5, borderBottom: '1px solid #e2e8f0' }}>Tutar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedMovements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, fontWeight: 700, color: '#64748b' }}>
                            {bankFilter !== 'all'
                              ? 'Seçilen banka ve tarih aralığına uygun hareket bulunamadı.'
                              : 'Bu filtreye uygun banka hareketi bulunamadı.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        pagedMovements.map((row) => {
                          const rowParties = resolveMovementDetail(row);
                          const taxId = resolveDisplayTaxIdForMovement(row, musteriTaxIndex);
                          const isIn = row.direction === 'in';
                          return (
                          <TableRow
                            key={`${row.bank || row.provider}-${row.id}`}
                            className="lb-data-row"
                            hover
                            onClick={() => { setSelectedMovement(row); setShowRawData(false); }}
                            selected={selectedMovement?.id === row.id && selectedMovement?.bank === row.bank}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: '#fff',
                              borderLeft: `3px solid ${isIn ? '#22c55e' : '#f87171'}`,
                              transition: 'background-color 120ms ease',
                              '&:hover': { bgcolor: isIn ? 'rgba(240,253,244,0.85)' : 'rgba(254,242,242,0.85)' },
                              '&.Mui-selected': { bgcolor: isIn ? 'rgba(220,252,231,0.9)' : 'rgba(254,226,226,0.9)' },
                              '& .MuiTableCell-root': { py: 1.25, borderBottom: '1px solid #f1f5f9' }
                            }}
                          >
                            <TableCell className="lb-cell-date" data-label="Tarih" sx={{ fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>{formatBankingDateTime(row)}</TableCell>
                            <TableCell className="lb-cell-bank" data-label="Banka" sx={{ fontWeight: 700, overflow: 'hidden' }}>
                              <Stack direction="row" alignItems="center" spacing={0.7} sx={{ minWidth: 0 }}>
                                <BankLogo bank={row.bank} bankLogoKey={row.bankLogoKey} size="md" />
                              </Stack>
                            </TableCell>
                            <TableCell className="lb-cell-direction" data-label="Yön">
                              <Chip
                                label={isIn ? 'Gelen' : 'Giden'}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  height: 22,
                                  fontSize: 11,
                                  bgcolor: isIn ? 'rgba(34,197,94,0.12)' : 'rgba(248,113,113,0.14)',
                                  color: isIn ? '#15803d' : '#b91c1c',
                                  border: `1px solid ${isIn ? 'rgba(34,197,94,0.28)' : 'rgba(248,113,113,0.32)'}`
                                }}
                              />
                            </TableCell>
                            <TableCell className="lb-cell-sender" data-label="Gönderen" sx={{ fontWeight: 700, color: rowParties.senderName ? '#0f172a' : '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12.5 }}>{rowParties.senderName || '—'}</TableCell>
                            <TableCell className="lb-cell-receiver" data-label="Alıcı" sx={{ fontWeight: 700, color: rowParties.receiverName ? '#0f172a' : '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12.5 }}>{rowParties.receiverName || '—'}</TableCell>
                            <TableCell
                              className="lb-cell-taxid"
                              data-label="TC / VKN"
                              sx={{
                                fontWeight: 700,
                                color: taxId.display ? '#0f172a' : '#cbd5e1',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 12,
                                fontVariantNumeric: 'tabular-nums'
                              }}
                              title={taxId.display ? `${taxId.label}: ${taxId.display}` : ''}
                            >
                              {taxId.display || '—'}
                              {(() => {
                                const durum = taxId.display ? mukellefMap.get(taxId.display) : null;
                                // Kayit yoksa tiklanabilir "?" rozeti: Kolaysoft'a sorar,
                                // sonuc sunucuda saklanir, ayni TC bir daha sorulmaz.
                                if (taxId.display && (!durum || durum.durum === MUKELLEF_DURUM.BILINMIYOR)) {
                                  const sorgulaniyor = mukellefSorgulanan === taxId.display;
                                  return (
                                    <Box
                                      component="span"
                                      className="lb-mukellef-rozet"
                                      title="Belge tipi bilinmiyor — sorgulamak için tıklayın"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (sorgulaniyor) return;
                                        setMukellefSorgulanan(taxId.display);
                                        sorgulaMukellef(taxId.display, sube)
                                          .then((sonuc) => {
                                            if (!sonuc) return;
                                            setMukellefMap((prev) => new Map(prev).set(sonuc.taxId, sonuc));
                                          })
                                          .catch(() => {})
                                          .finally(() => setMukellefSorgulanan(null));
                                      }}
                                      sx={{
                                        display: 'inline-block',
                                        ml: 0.7,
                                        px: 0.6,
                                        py: '1px',
                                        borderRadius: '5px',
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        lineHeight: 1.5,
                                        verticalAlign: 'middle',
                                        cursor: 'pointer',
                                        bgcolor: '#f1f5f9',
                                        color: '#475569',
                                        border: '1px solid #e2e8f0',
                                        '&:hover': { bgcolor: '#e2e8f0' }
                                      }}
                                    >
                                      {sorgulaniyor ? '…' : '?'}
                                    </Box>
                                  );
                                }
                                if (!durum || durum.durum === MUKELLEF_DURUM.BILINMIYOR) return null;
                                const stil = mukellefRozetStili(durum.durum);
                                const sonTarih = durum.durum === MUKELLEF_DURUM.EFATURA ? durum.lastEfaturaDate : durum.lastEarsivDate;
                                return (
                                  <Box
                                    component="span"
                                    className="lb-mukellef-rozet"
                                    title={`Geçmiş faturalara göre${sonTarih ? ` (son: ${sonTarih})` : ''}`}
                                    sx={{
                                      display: 'inline-block',
                                      ml: 0.7,
                                      px: 0.6,
                                      py: '1px',
                                      borderRadius: '5px',
                                      fontSize: 9.5,
                                      fontWeight: 800,
                                      lineHeight: 1.5,
                                      verticalAlign: 'middle',
                                      bgcolor: stil.bg,
                                      color: stil.color,
                                      border: `1px solid ${stil.border}`
                                    }}
                                  >
                                    {stil.label}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="lb-cell-desc" data-label="Açıklama" sx={{ maxWidth: 430, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#475569', fontSize: 12.5 }}>{row.description || '—'}</TableCell>
                            <TableCell className="lb-cell-amount" data-label="Tutar" align="right" sx={{ fontWeight: 900, color: isIn ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 2.5, fontVariantNumeric: 'tabular-nums' }}>{isIn ? '+' : '−'}{formatMoney(row.amount)} TL</TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  className="lb-pagination"
                  component="div"
                  count={filteredMovements.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  labelRowsPerPage="Sayfa başı"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count} işlem`}
                  sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}
                />
              </Paper>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog className="lb-range-dialog" open={dateRangeDialogOpen} onClose={() => setDateRangeDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ className: 'lb-range-dialog-paper' }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Tarih Aralığı Seç</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <TextField
              label="Başlangıç Tarihi"
              type="date"
              size="small"
              value={rangeStartDate}
              onChange={(e) => setRangeStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Bitiş Tarihi"
              type="date"
              size="small"
              value={rangeEndDate}
              onChange={(e) => setRangeEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateRangeDialogOpen(false)} sx={{ textTransform: 'none' }}>İptal</Button>
          <Button
            variant="contained"
            disabled={!rangeStartDate || !rangeEndDate}
            onClick={() => {
              setTimeFilter('range');
              setPage(0);
              setDateRangeDialogOpen(false);
            }}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Uygula
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className="lb-detail-dialog"
        open={!!selectedMovement}
        onClose={() => setSelectedMovement(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: 'lb-detail-dialog-paper',
          sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 30px 70px rgba(15,23,42,0.38)' }
        }}
      >
        {selectedMovement ? (
          <>
            <Box
              sx={{
                position: 'relative',
                p: 2.4,
                color: '#fff',
                background:
                  selectedMovement.direction === 'in'
                    ? 'linear-gradient(135deg, #065f46 0%, #10b981 100%)'
                    : 'linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: -30,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.10)'
                }}
              />
              <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
                <BankLogo bank={selectedMovement.bank} bankLogoKey={selectedMovement.bankLogoKey} size="xl" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', opacity: 0.85 }}>İŞLEM DETAYI</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.92 }}>
                    {selectedMovement.bankLabel || selectedMovement.bankName || ''} · {formatBankingDateTime(selectedMovement)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={selectedMovement.direction === 'in' ? 'GELEN' : 'GİDEN'}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.24)', color: '#fff', fontWeight: 900, letterSpacing: '0.06em', '& .MuiChip-label': { color: '#fff' } }}
                />
              </Stack>
              <Typography sx={{ position: 'relative', mt: 1.6, fontSize: 30, fontWeight: 900, lineHeight: 1 }}>
                {selectedMovement.direction === 'in' ? '+' : '-'}{formatMoney(selectedMovement.amount)} {selectedMovement.currency || 'TL'}
              </Typography>
            </Box>

            <DialogContent dividers sx={{ p: 0 }}>
              <Stack divider={<Box sx={{ borderBottom: '1px solid #eef2f7' }} />}>
                {detailRow('Banka', parties.bankName || selectedMovement.bankLabel || selectedMovement.bankName || '-')}
                {detailRow('Şube', branchLabel(sube))}
                {detailRow('Yön', parties.directionLabel)}
                {detailRow('Tutar', `${selectedMovement.direction === 'in' ? '+' : '-'}${formatMoney(selectedMovement.amount)} ${selectedMovement.currency || 'TL'}`)}
                {detailRow('İşlem Tarihi', parties.transactionDate)}
                {detailRow('İşlem Saati', parties.transactionTime)}
                {detailRow('Valör Tarihi', parties.valueDate)}
                {detailRow('Gönderen Adı', parties.senderName || '-')}
                {detailRow('Alıcı Adı', parties.receiverName || '-')}
                {detailRow(
                  selectedTaxId.label || 'TC / VKN',
                  selectedTaxId.display || '—',
                  Boolean(selectedTaxId.display)
                )}
                {detailRow('Gönderen IBAN / Hesap', parties.senderIban || parties.senderAccount || '-', true)}
                {detailRow('Alıcı IBAN / Hesap', parties.receiverIban || parties.receiverAccount || '-', true)}
                {detailRow('Referans No', selectedMovement.reference || selectedMovement.externalTxId || '-', true)}
                {detailRow('Açıklama', selectedMovement.description || '-')}
              </Stack>
              <Box sx={{ px: 2.2, py: 1.4 }}>
                <Button
                  size="small"
                  onClick={() => setShowRawData((prev) => !prev)}
                  endIcon={showRawData ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', justifyContent: 'flex-start', px: 0, fontWeight: 900, color: '#475569' }}
                >
                  Ham Veri (rawData)
                </Button>
                <Collapse in={showRawData}>
                  <Paper variant="outlined" sx={{ mt: 0.6, p: 1, bgcolor: '#0f172a', borderColor: '#1e293b', borderRadius: 2, maxHeight: 200, overflow: 'auto' }}>
                    <Typography component="pre" sx={{ m: 0, fontSize: 11, color: '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                      {selectedMovement.rawData ? JSON.stringify(selectedMovement.rawData, null, 2) : 'rawData yok'}
                    </Typography>
                  </Paper>
                </Collapse>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.2, py: 1.4, bgcolor: '#f8fafc' }}>
              <Button
                onClick={() => setSelectedMovement(null)}
                variant="contained"
                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 2.6, bgcolor: '#1f2937', '&:hover': { bgcolor: '#111827' } }}
              >
                Kapat
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
      <style>{`
        /* --- MOBİL: Canlı Banka (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 600px) {
          .lb-modal .MuiDialog-container {
            align-items: stretch !important;
          }

          .lb-toolbar {
            flex-wrap: wrap !important;
            min-height: auto !important;
            padding: 8px 10px !important;
            gap: 6px !important;
            row-gap: 6px !important;
          }

          .lb-toolbar-title {
            font-size: 15px !important;
            flex: 1 1 auto !important;
            min-width: 0 !important;
          }

          .lb-toolbar-warning {
            order: 5 !important;
            flex: 1 1 100% !important;
            width: 100% !important;
            justify-content: center !important;
          }

          .lb-toolbar-sync {
            order: 10 !important;
            width: 100% !important;
            margin: 0 !important;
            font-size: 10px !important;
            text-align: left !important;
          }

          .lb-toolbar-close {
            margin-left: auto !important;
          }

          .lb-body {
            height: auto !important;
            min-height: calc(100svh - 56px) !important;
            max-height: none !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            padding: 8px 6px 16px !important;
            -webkit-overflow-scrolling: touch;
          }

          .lb-layout {
            height: auto !important;
            min-height: 0 !important;
            grid-template-rows: auto auto !important;
            gap: 10px !important;
          }

          .lb-summary-row,
          .lb-main-row {
            grid-template-columns: 1fr !important;
            height: auto !important;
            gap: 10px !important;
            width: 100% !important;
          }

          .lb-metrics-wrap,
          .lb-branches-wrap,
          .lb-transactions-panel {
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }

          .lb-metrics-grid {
            width: 100% !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
          }

          .lb-metrics-grid .lb-bank-balance-card {
            grid-column: span 1 !important;
          }

          .lb-branches-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }

          .lb-bank-filters {
            width: 100% !important;
            overflow-x: auto !important;
          }

          .lb-bank-toggle .MuiToggleButton-root {
            min-width: 0 !important;
            font-size: 11px !important;
            padding: 4px 10px !important;
          }

          .lb-transactions-panel {
            grid-template-rows: auto 1fr auto !important;
            min-height: 420px !important;
            overflow: visible !important;
          }

          .lb-filters {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            padding: 10px 8px !important;
            height: auto !important;
          }

          .lb-filter-gap,
          .lb-filter-gap-sm,
          .lb-filter-gap-lg {
            display: none !important;
            width: 0 !important;
          }

          .lb-search-field {
            width: 100% !important;
          }

          .lb-search-field .MuiInputBase-input {
            font-size: 16px !important;
          }

          .lb-direction-filters,
          .lb-time-filters {
            width: 100% !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          .lb-direction-toggle,
          .lb-time-toggle {
            flex-wrap: nowrap !important;
          }

          .lb-direction-toggle .MuiToggleButton-root,
          .lb-time-toggle .MuiToggleButton-root {
            min-width: 0 !important;
            font-size: 11px !important;
            padding: 4px 8px !important;
          }

          .lb-range-btn,
          .lb-clear-btn {
            width: 100% !important;
            min-height: 38px !important;
          }

          .lb-time-filters {
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .lb-table-wrap {
            overflow: visible !important;
            max-height: none !important;
          }

          .lb-table {
            display: block !important;
            min-width: 0 !important;
            width: 100% !important;
          }

          .lb-table thead {
            display: none !important;
          }

          .lb-table tbody {
            display: block !important;
            width: 100% !important;
          }

          .lb-table tbody tr.lb-data-row {
            display: grid !important;
            grid-template-columns: 1fr auto;
            grid-template-rows: auto auto auto auto auto;
            gap: 4px 8px;
            padding: 10px 10px !important;
            margin-bottom: 0 !important;
            border-bottom: 1px solid #e2e8f0 !important;
            height: auto !important;
            max-height: none !important;
          }

          .lb-table tbody tr.lb-data-row .MuiTableCell-root {
            display: block !important;
            padding: 0 !important;
            border: none !important;
            height: auto !important;
            max-height: none !important;
            max-width: none !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: unset !important;
          }

          .lb-table tbody tr.lb-data-row .MuiTableCell-root::before {
            display: none !important;
          }

          .lb-cell-direction {
            grid-column: 1 !important;
            grid-row: 1 !important;
          }

          .lb-cell-amount {
            grid-column: 2 !important;
            grid-row: 1 !important;
            text-align: right !important;
            padding-right: 12px !important;
            font-size: 14px !important;
            align-self: center !important;
          }

          .lb-cell-date {
            grid-column: 1 / 3 !important;
            grid-row: 2 !important;
            font-size: 11px !important;
            color: #64748b !important;
            font-weight: 600 !important;
          }

          .lb-cell-bank {
            grid-column: 1 / 3 !important;
            grid-row: 3 !important;
          }

          .lb-cell-desc {
            grid-column: 1 / 3 !important;
            grid-row: 4 !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            color: #0f172a !important;
            word-break: break-word !important;
          }

          .lb-cell-ref {
            grid-column: 1 !important;
            grid-row: 5 !important;
            font-size: 10px !important;
            color: #64748b !important;
            word-break: break-word !important;
          }

          .lb-cell-status {
            grid-column: 2 !important;
            grid-row: 5 !important;
            text-align: right !important;
          }

          .lb-pagination {
            overflow-x: auto !important;
          }

          .lb-pagination .MuiTablePagination-toolbar {
            flex-wrap: wrap !important;
            justify-content: center !important;
            gap: 4px !important;
            min-height: 48px !important;
            padding: 4px 8px !important;
          }

          .lb-range-dialog-paper,
          .lb-detail-dialog-paper {
            width: calc(100vw - 16px) !important;
            max-width: calc(100vw - 16px) !important;
            margin: 8px !important;
          }

          .lb-range-dialog .MuiInputBase-input,
          .lb-detail-dialog .MuiInputBase-input {
            font-size: 16px !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

export default LiveBankModal;
